import { ee } from '../../services/earth-engine';
import { computeBearing, computeDisplacement } from '../geodesy';
import * as Metadata from '../../common/metadata';
import { getDate } from '../utils';
import { callback, evaluate } from '../../store/tools/effects';
import { ContactSupportOutlined } from '@material-ui/icons';

/**
 * @function identifyWaterFeature
 * @description
 * This function aims to indentify water feature by making partitions in the raster *image*
 * using the water index and the threshold function, then reduces the output raster to vector,
 * corresponding to the water body in the given *image*.
 * @param {ee.Image} image
 * @param {ee.Geometry} geometry
 * @param {Number} scale
 * @param {Object} bands
 * @param {Function} thresholdFn - Threshold supplier function
 * **Called by:
 * @see {extractShoreline}
 * @memberof module:SHORELINE
 * @returns {ee.Geometry} the identified water body
 */
export const identifyWaterFeature = (image, geometry, scale, bands, thresholdFn) => {
  const internalBandName = 'NDWI';

  const ndwi = ee.Image(image).normalizedDifference([bands.green, bands.nir]).rename(internalBandName);
  let threshold = ee.Number(0.01);
  try {
    threshold = thresholdFn(ndwi, internalBandName, geometry, scale);
  } catch (e) {
    console.log('error calculating  threshold - using value', threshold);
  }
  const water = ee.Feature(
    ndwi
      .clip(geometry)
      .gt(threshold)
      .reduceToVectors({ scale, maxPixels: 1e12 })
      .filter(ee.Filter.eq('label', 1))
      .limit(1, 'count', false)
      .first(),
  );
  let waterLocal = water.getInfo();
  let result = null;
  if (waterLocal !== null) {
    result = water.geometry();
  } else {
    console.log('water is null (lower than threshold) - image excluded by threshold');
  }
  return result;
};

/**
 * @function removeShorelineNoise
 * @description Removes noises from the shoreline by keeping only  the polygons that have the most intersections with the transects.
 * This filtering process identifies the main shoreline by calculating intersection weights and selecting the segment with maximum weight.
 *
 * @param {ee.Geometry.MultiLineString} shorelines -  The raw shoreline geometry that may contain noise
 * @param {ee.Geometry.MultiLineString|ee.FeatureCollection} transects - Reference transects used to weight segments
 * **Called by:
 * @see {deriveShoreline}
 * @memberof module:SHORELINE
 * @returns {ee.Feature<ee.Geometry.LineString>} Cleaned shoreline without noise
 */
export const removeShorelineNoise = (shorelines, transects) => {
  const coordinates = ee.Geometry(shorelines).coordinates();

  const guard = ee.List(
    ee.Algorithms.If(ee.Geometry(shorelines).type().compareTo('MultiLineString').eq(0), coordinates, [coordinates]),
  );

  const weighted = guard.map((segment) => {
    const linestring = ee.Geometry.LineString(segment);
    return ee.Feature(linestring, {
      weight: linestring.intersection(transects).coordinates().size(),
    });
  });

  return ee.Feature(ee.FeatureCollection(weighted).limit(1, 'weight', false).first()).geometry();
};

/**
 *  @function gaussianKernel
 * @description
 * Creates a gaussian kernel controlled by *size*, *mean* and *sigma*.
 * The output kernel size is always odd to ensure a center point.
 *
 * @param {Number} size - The size of the kernel (will be rounded to nearest odd number)
 * @param {Number} mean - Center of the normal distribution (typically 0)
 * @param {Number} sigma - Standard deviation, controls the spread of the bell curve
 * **Called by:
 * @see {linearGaussianFilter}
 * @memberof module:SHORELINE
 * @returns {ee.List<Number>} Normalized gaussian kernel where all values sum to 1
 */
export const gaussianKernel = (size, mean, sigma) => {
  const gaussianCurve = (x, mean, sigma) => {
    const divider = ee.Number(sigma).multiply(ee.Number(2).multiply(Math.PI).sqrt());
    const exponent = ee.Number(-1).multiply(
      ee
        .Number(x)
        .subtract(mean)
        .pow(2)
        .divide(ee.Number(2).multiply(ee.Number(sigma).pow(2))),
    );

    return ee.Number(1).divide(divider).multiply(exponent.exp());
  };

  const half = ee.Number(size).divide(2).floor();

  const begin = ee.Number(0).subtract(half),
    end = ee.Number(0).add(half);

  const kernel = ee.List.sequence(begin, end).map((i) => gaussianCurve(i, mean, sigma));

  const sum = kernel.reduce(ee.Reducer.sum());

  const normalizedKernel = kernel.map((val) => ee.Number(val).divide(sum));

  return normalizedKernel;
};

/**
 * @function linearGaussianFilter
 * @description
 * Convolves over the list *coordinates* producing local averages weighted by the produced kernel of *samples*, *mean*, *sd*.
 * The result is a smoothed list (often linestring) with removed noises such as pixel-induced staircase effect.
 * 1. Builds the gaussian weighting kernel for the given samples/mean/sd.
 * 2. Slides a kernel-sized window over the coordinates, pairing each point in the window with its kernel weight.
 * 3. Multiplies each point's longitude/latitude by its weight and sums them into a single smoothed point per window.
 * 4. Keeps the original first/last points untouched and returns the original coordinates unchanged
 *    when there aren't enough points to fill a full kernel window.
 *
 * @param {ee.List} coordinates - List containing the path coordinates to be smoothed
 * @param {ee.Number} samples - Size of the kernel
 * @param {ee.Number} mean  -Center of the normal distribution
 * @param {ee.Number} sd - Standard Deviation, controls the size of the bell-shaped curve
 * @see {@link gaussianKernel} - builds the weighting kernel used to smooth the coordinates.
 * **Called by:
 * @see {deriveShoreline}
 * @memberof module:SHORELINE
 * @returns {ee.List<Number>} Smoothed coordinates with reduced noise
 */
export const linearGaussianFilter = (coordinates, samples = 3, mean = 0, sd = 1) => {
  const coordinateList = ee.List(coordinates);

  const kernelSize = ee.Number(samples);
  const kernelMean = ee.Number(mean);
  const kernelSd = ee.Number(sd);
  const kernel = gaussianKernel(kernelSize, kernelMean, kernelSd);

  const first = coordinateList.reduce(ee.Reducer.first()),
    last = coordinateList.reduce(ee.Reducer.last());

  const sequence = ee.List.sequence(ee.Number(0), coordinateList.length().subtract(kernelSize));

  const path = sequence.map((index) => {
    const interval = coordinateList.slice(ee.Number(index), ee.Number(index).add(kernelSize)).zip(kernel);

    const gaussian = interval.map((element) => {
      const asList = ee.List(element);

      const point = ee.List(asList.get(0));
      const weight = ee.Number(asList.get(1));

      return point.map((value) => ee.Number(value).multiply(weight));
    });

    const smoothenLong = gaussian.map((point) => ee.List(point).get(0)).reduce(ee.Reducer.sum());
    const smoothenLat = gaussian.map((point) => ee.List(point).get(1)).reduce(ee.Reducer.sum());

    return ee.List([smoothenLong, smoothenLat]);
  });

  const smoothen = ee.List([]).add(first).cat(path).add(last);

  return ee.Algorithms.If(coordinateList.size().lte(kernelSize), coordinateList, smoothen);
};

/**
 * @function thresholdingAlgorithm
 * @description
 * Given a histogram, find the appropriate thresholds for *count* classes
 * @param {ee.Dictionary} histogram
 * @param {Number} count
 * **Called by:
 * @see {selectThreshold}
 * @memberof module:SHORELINE
 * @returns {ee.Number} the optimal threshold
 */
export const thresholdingAlgorithm = (histogram, count) => {
  let eeHistogram = ee.Dictionary(histogram);
  let eeHistogramInfo = eeHistogram.getInfo();
  if (typeof eeHistogramInfo.bucketMeans === 'undefined') {
    console.warn('Error calculating thresholdingAlgorithm');
    throw new Error('Error calculating thresholdingAlgorithm');
  }
  const counts = ee.Array(ee.Dictionary(histogram).get('histogram'));
  let bucketMeans = ee.Dictionary(histogram).get('bucketMeans');
  const means = ee.Array(bucketMeans);
  const size = means.length().get([0]);
  const total = counts.reduce(ee.Reducer.sum(), [0]).get([0]);
  const sum = means.multiply(counts).reduce(ee.Reducer.sum(), [0]).get([0]);
  const mean = sum.divide(total);
  const bss = ee.List.sequence(1, size).map((i) => {
    const aCounts = counts.slice(0, 0, i);
    const aCount = aCounts.reduce(ee.Reducer.sum(), [0]).get([0]);
    const aMeans = means.slice(0, 0, i);
    const aMean = aMeans.multiply(aCounts).reduce(ee.Reducer.sum(), [0]).get([0]).divide(aCount);
    const bCount = total.subtract(aCount);
    const bMean = sum.subtract(aCount.multiply(aMean)).divide(bCount);
    return aCount.multiply(aMean.subtract(mean).pow(2)).add(bCount.multiply(bMean.subtract(mean).pow(2)));
  });

  return means.sort(bss).get([-1]);
};

/**
 * @function selectThreshold
 *  @description
 * Selects the threshold function according to the input *threshold*.
 * This will be refactored to use something other than a number to choose the intended threshold function.
 * @param {Number} threshold
 * @see {@link thresholdingAlgorithm} - used by the histogram-based thresholding branches.
 * **Called by:
 * @see {deriveShoreline}
 * @memberof module:SHORELINE
 * @returns {Function} the thresholding function
 */
const selectThreshold = (threshold) => {
  const maxPixels = 1e12;
  switch (threshold) {
    case 0.0:
      return (image, band, _, scale) => {
        scale = 30;

        let min = image
          .reduceRegion({
            reducer: ee.Reducer.min(),
            scale,
            bestEffort: true,
          })
          .get(band);

        let returnValue = thresholdingAlgorithm(
          image
            .reduceRegion({
              reducer: ee.Reducer.histogram(),
              scale,
              bestEffort: true,
            })
            .get(band),
          2,
        );
        return returnValue;
      };
    case -1.0:
      return (image, band, _, scale) => {
        scale = 300;
        let returnValue = thresholdingAlgorithm(
          image
            .reduceRegion({
              reducer: ee.Reducer.histogram(),
              scale,
              maxPixels: maxPixels,
            })
            .get(band),
          3,
        );
        return returnValue;
      };
    default:
      return () => threshold;
  }
};

/**
 * @function extractShoreline
 * @description
 * Extracts the raw shoreline of the given *image*, by identifying the water feature (body) and converting it into linestrings.
 *
 * @param {ee.Image} image - Satellite image
 * @param {ee.Geometry} geometry -AOI
 * @param {Number} scale
 * @param {Object} bands
 * @param {Function} thresholdFn
 *
 * @see{@link identifyWaterFeature} - gets water segment for the given image
 * **Called by:
 * @see {deriveShoreline}
 * @memberof module:SHORELINE
 * @returns {ee.Geometry.MultiLineString} Raw shoreline as MultiLineString, or null if water detection fails
 */
export const extractShoreline = (image, geometry, scale, bands, thresholdFn) => {
  const waterSegment = identifyWaterFeature(image, geometry, scale, bands, thresholdFn);
  if (waterSegment === null) return null;
  return ee.Geometry.MultiLineString(waterSegment.coordinates().get(0)).intersection(geometry.buffer(-10));
};

/**
 * @function deriveShoreline
 * @description
 * Fully derives the shoreline using the satellite *image*. The satellite derived shoreline is a single smoothed linestring
 * without noisy features (polygons that are not the shoreline).
 * @param {ee.Image} image - Satellite image to extract shoreline from
 * @param {ee.Geometry} geometry - AOI (Area of Interest)
 * @param {Number} scale
 * @param {Object} bands
 * @param {Number} threshold
 * @param {ee.FeatureCollection|ee.List} transects
 * @param {Number} opticalResolution - Maximum pixel displacement for image co-registration
 *
 * @see{@link extractShoreline}
 * @see{@link removeShorelineNoise}
 * @see{@link linearGaussianFilter}
 * @see{@link selectThreshold}
 * **Called by:
 * @see {deriveShorelines}
 * @memberof module:SHORELINE
 * @returns {ee.Feature} Feature containing smoothed shoreline LineString
 */

let firstImage = null;
export const deriveShoreline = (image, geometry, scale, bands, threshold, transects, opticalResolution) => {
  const transectsGeometry = ee.FeatureCollection(transects).geometry();

  if (firstImage === null) {
    firstImage = image;
    firstImage = firstImage.select(bands.red, bands.green, bands.blue, bands.nir);
  } else {
    let maxOffset = opticalResolution;
    image = image.select(bands.red, bands.green, bands.blue, bands.nir);
    image = image.register(firstImage, maxOffset);
  }

  const shoreline = extractShoreline(image, geometry, scale, bands, selectThreshold(threshold));
  if (shoreline === null) {
    let imgInfo = image.getInfo();
    let errors = JSON.parse(window.sessionStorage.getItem('shorelineImagesErrors'));
    let dtTimeStamp = imgInfo.properties['system:time_start'].value;
    errors.push({
      message: 'Image excluded by threshold',
      date: dtTimeStamp,
      dateFormated: new Date(dtTimeStamp).toISOString(),
    });
    window.sessionStorage.setItem('shorelineImagesErrors', JSON.stringify(errors));
    console.error('shoreline with problem (threshold): returned null', imgInfo);
    return null;
  }
  const enhanced = removeShorelineNoise(shoreline, transectsGeometry);
  const smoothen = linearGaussianFilter(enhanced.coordinates());
  let result = ee.Feature(ee.Geometry.LineString(smoothen), {
    [Metadata.TIME_START]: ee.Date(getDate(image)).format(null, 'UTC'),
  });
  return result;
};

/**
 * @function deriveShorelines
 * @description
 * Acquires and extracts shorelines given an array of dates
 *
 * @param {Array} dates - a list containing the dates of the images to be analysed
 * @param {Object} satellite - information about the satellite
 * @param {ee.Geometry} geometry - Area of Interest
 * @param {Number} scale - Sampling scale in meters
 * @param {ee.Number} threshold
 * @param {ee.FeatureCollection} transects - a FeatureCollection of polygons
 * @param {Function} [acquireImage] - (mission, date, geometry) => ee.Image. Defaults
 *   to `mission.algorithms.acquire`, which filters a ±1-minute window around the
 *   *exact* timestamp it's given — fine for callers whose dates carry a real
 *   acquisition time (standard/Shore-R), but it reliably finds nothing (and GEE
 *   throws `List.get: List is empty`) for day-only dates (midnight), like
 *   Shore-S's. Pass `acquireDayMosaic` (`algorithms/satellite/shoreSAvailability.js`)
 *   for day-only callers instead.
 *
 * @see {@link deriveShoreline}
 * **Called by:
 * @see {deriveInChunks}
 * @memberof module:SHORELINE
 * @returns {Array<ee.Feature>} Array of shoreline features
 */
export const deriveShorelines = async (
  dates,
  satellite,
  geometry,
  scale,
  threshold,
  transects,
  acquireImage = (mission, date, geom) => mission.algorithms.acquire(date, geom)
) => {
  const data = dates.map((value) => {
    const mission = satellite.missions.find((mission) => mission.name === value.name);
    try {
      return deriveShoreline(
        ee.Image(acquireImage(mission, value.date, geometry)),
        geometry,
        scale,
        mission.bands,
        threshold,
        transects,
        mission.opticalResolution,
      );
    } catch (e) {
      console.error('deriveShorelines  - Error on (ignoring image)', value, 'returning null', e);
      return null;
    }
  });

  return data;
};

/**
 * @function expandCoastline
 * @description
 * Expands the given *coordinates* by an amount *distance* to create a buffer polygon.
 *
 * @param {ee.List} coordinates - List of coordinates representing the coastline path
 * @param {Number} distance - Distance to expand the coastline in meters
 * @see{@link rectifyJunctions}
 * **Called by:
 * Not currently called anywhere in the codebase.
 * @memberof module:SHORELINE
 * @returns {ee.Feature} Feature containing a Polygon geometry representing the expanded coastline area
 */
export const expandCoastline = (coordinates, distance) => {
  coordinates = ee.List(coordinates);
  const neighbors = ee.List(coordinates.slice(1));

  let displacedSegments = coordinates.zip(neighbors).map((segment) => {
    segment = ee.List(segment);
    const current = ee.List(segment.get(0));
    const neighbor = ee.List(segment.get(1));
    const bearing = computeBearing(current.get(0), current.get(1), neighbor.get(0), neighbor.get(1));

    const p1 = computeDisplacement(current.get(0), current.get(1), bearing.add(Math.PI * 0.5), distance);
    const p2 = computeDisplacement(neighbor.get(0), neighbor.get(1), bearing.add(Math.PI * 0.5), distance);
    return ee.List([p1, p2]);
  });

  displacedSegments = rectifyJunctions(displacedSegments, coordinates);
  const combined = coordinates.reverse().splice(0, 0, displacedSegments);

  return ee.Feature(ee.Geometry.Polygon([combined]));
};

/**
 * @function rectifyJunctions
 * @description
 * Normalizes the junctions *segments*
 * @param {ee.List} segments - [[p1, p2], [p3, p4], ...]
 * @param {ee.List} originalCoordinates - Original coordinates before displacement, used as reference points
 * **Called by:
 * @see {expandCoastline}
 * @memberof module:SHORELINE
 * @returns {ee.List} Rectified coordinates list with properly positioned junction points
 */
const rectifyJunctions = (segments, originalCoordinates) => {
  segments = ee.List(segments);
  originalCoordinates = ee.List(originalCoordinates).slice(1);
  const neighbors = ee.List(segments.slice(1));

  let processed = ee.List.sequence({ start: 0, count: neighbors.size() }).map((index) => {
    const currentSegment = ee.List(segments.get(index));
    const neighborSegment = ee.List(neighbors.get(index));
    const original = ee.List(originalCoordinates.get(index));

    const previous = ee.List(currentSegment.get(1));
    const next = ee.List(neighborSegment.get(0));

    const alpha = computeBearing(original.get(0), original.get(1), previous.get(0), previous.get(1));
    const beta = computeBearing(original.get(0), original.get(1), next.get(0), next.get(1));
    const difference = alpha.subtract(beta);
    const halfDifference = difference.divide(2);
    const theta = beta.add(halfDifference);

    const adjacent = ee.Geometry.Point(original).distance(ee.Geometry.Point(next));
    const length = ee.Number(adjacent).divide(halfDifference.cos());

    const centroid = computeDisplacement(original.get(0), original.get(1), theta, length);

    return centroid;
  });

  const first = ee.List(ee.List(segments.get(0)).get(0));
  const last = ee.List(ee.List(segments.get(-1)).get(1));

  processed = ee.List(processed);
  processed = processed.insert(0, ee.List([first.get(0), first.get(1)]));
  processed = processed.add(ee.List([last.get(0), last.get(1)]));

  return processed;
};
